/* eslint-disable @typescript-eslint/no-explicit-any -- Recharts Sankey data and render props are loosely typed */
import type { SVGProps } from "react";
import { ResponsiveContainer, Sankey, Tooltip } from "recharts";

import { formatCurrency } from "@/lib/formatters";
import { cn } from "@/lib/utils";

import type { BudgetPlanNode } from "../lib/build-budget-plan";

/** SVG <text> inside Recharts often ignores Tailwind `fill-*`; use theme variables. */
const SANKEY_LABEL_FILL = "hsl(var(--foreground))";
const SANKEY_AMOUNT_FILL = "hsl(var(--foreground) / 0.78)";

function nodeFill(nodeType: string): string {
  switch (nodeType) {
    case "income":
      return "#86efac";
    case "budget":
      return "#a5b4fc";
    case "expense_group":
      return "#fda4af";
    case "investment_group":
      return "#93c5fd";
    case "remaining_group":
      return "#d8b4fe";
    case "remaining":
      return "#e9d5ff";
    case "book":
      return "#bfdbfe";
    case "expense":
      return "#fecdd3";
    default:
      return "#cbd5e1";
  }
}

function nodeAccent(nodeType: string): string {
  switch (nodeType) {
    case "income":
      return "#16a34a";
    case "budget":
      return "#6366f1";
    case "expense_group":
      return "#e11d48";
    case "investment_group":
      return "#2563eb";
    case "remaining_group":
      return "#9333ea";
    case "remaining":
      return "#a855f7";
    case "book":
      return "#0284c7";
    case "expense":
      return "#fb7185";
    default:
      return "#64748b";
  }
}

function linkStrokeColor(
  sourceType: string | undefined,
  targetType: string | undefined,
): string {
  if (targetType === "book" || sourceType === "book") return "#60a5fa";
  if (targetType === "budget") return "#34d399";
  if (targetType === "expense_group" || sourceType === "expense_group") {
    return "#fb7185";
  }
  if (
    targetType === "investment_group" ||
    sourceType === "investment_group"
  ) {
    return "#60a5fa";
  }
  if (targetType === "remaining_group" || sourceType === "remaining_group") {
    return "#c084fc";
  }
  return "#94a3b8";
}

function truncateLabel(text: string, maxChars: number): string {
  const value = text.trim();
  if (value.length <= maxChars) {
    return value;
  }

  return `${value.slice(0, Math.max(1, maxChars - 1))}…`;
}

export function BudgetSankeyTooltip({
  active,
  payload,
  currencyCode,
}: {
  active?: boolean;
  payload?: Array<{
    payload?: {
      name?: string;
      label?: string;
      value?: number;
      amount?: number;
    };
  }>;
  currencyCode: string;
}) {
  if (!active || !payload?.length) {
    return null;
  }

  const item = payload[0]?.payload;
  if (!item) {
    return null;
  }

  return (
    <div className="rounded-lg border bg-background px-3 py-2 shadow-md">
      <p className="text-sm font-medium">{item.name || item.label}</p>
      <p className="text-xs text-muted-foreground">
        {formatCurrency(item.amount ?? item.value ?? 0, currencyCode)}
      </p>
    </div>
  );
}

type SankeyLinkPayload = {
  source: BudgetPlanNode & { value?: number };
  target: BudgetPlanNode & { value?: number };
};

function BudgetSankeyLink(
  props: {
    sourceX: number;
    sourceY: number;
    sourceControlX: number;
    targetX: number;
    targetY: number;
    targetControlX: number;
    linkWidth: number;
    payload: SankeyLinkPayload;
  } & Omit<SVGProps<SVGPathElement>, "d" | "fill" | "stroke" | "strokeWidth">,
) {
  const {
    sourceX,
    sourceY,
    sourceControlX,
    targetX,
    targetY,
    targetControlX,
    linkWidth,
    payload,
    ...rest
  } = props;

  const stroke = linkStrokeColor(
    payload?.source?.type,
    payload?.target?.type,
  );
  const d = `M${sourceX},${sourceY} C${sourceControlX},${sourceY} ${targetControlX},${targetY} ${targetX},${targetY}`;

  return (
    <>
      <path
        d={d}
        fill="none"
        stroke="rgba(148, 163, 184, 0.16)"
        strokeWidth={linkWidth + 2}
        strokeLinecap="butt"
      />
      <path
        className="recharts-sankey-link transition-[stroke-opacity] hover:stroke-opacity-90"
        d={d}
        fill="none"
        stroke={stroke}
        strokeWidth={linkWidth}
        strokeOpacity={0.42}
        strokeLinecap="butt"
        {...rest}
      />
    </>
  );
}

type SankeyNodePayload = BudgetPlanNode & {
  value?: number;
  /** Recharts internal: incoming links; empty ⇒ leftmost column (sources). */
  sourceNodes?: number[];
  /** Recharts internal: outgoing links; empty ⇒ rightmost column. */
  targetNodes?: number[];
};

function BudgetSankeyNode({
  x,
  y,
  width,
  height,
  payload,
  onNodeClick,
  currencyCode,
}: {
  x: number;
  y: number;
  width: number;
  height: number;
  payload?: SankeyNodePayload;
  onNodeClick: (payload: { id?: string }) => void;
  currencyCode: string;
}) {
  const nodeType = payload?.type ?? "default";
  const isBook = nodeType === "book";
  const fill = nodeFill(nodeType);
  const accent = nodeAccent(nodeType);

  const rectWidth = Math.max(width, 10);
  const rectHeight = Math.max(height, 10);
  const midY = y + rectHeight / 2;
  const label = payload?.label ?? "";
  const amountText = formatCurrency(
    payload?.amount ?? payload?.value ?? 0,
    currencyCode,
  );

  const isFirstColumn = !payload?.sourceNodes?.length;
  const isLastColumn = !payload?.targetNodes?.length;
  const isMiddleColumn = !isFirstColumn && !isLastColumn;
  const labelGap = isMiddleColumn ? 12 : 16;
  const textAnchor: "start" | "end" = isFirstColumn ? "start" : "end";
  const connectorStartX = isFirstColumn ? x + rectWidth : x;
  const connectorEndX = isFirstColumn
    ? x + rectWidth + labelGap - 6
    : x - labelGap + 6;
  const textX = isFirstColumn ? x + rectWidth + labelGap : x - labelGap;
  const showAnyText = rectHeight >= 10;
  const showBookHint = isBook && !isMiddleColumn;
  const labelY = showBookHint ? midY - 2 : midY - 4;
  const amountY = showBookHint ? midY + 12 : midY + 10;
  const eyebrowY = midY - 14;
  const maxLabelChars = isMiddleColumn ? 10 : 18;
  const displayLabel = truncateLabel(label, maxLabelChars);

  return (
    <g
      onClick={() => (isBook ? onNodeClick(payload ?? {}) : undefined)}
      className={cn("transition-all", isBook && "cursor-pointer")}
    >
      <rect
        x={x}
        y={y}
        width={rectWidth}
        height={rectHeight}
        fill={fill}
        rx={4}
        stroke={accent}
        strokeOpacity={isBook ? 0.45 : 0.22}
        strokeWidth={isBook ? 1.5 : 1}
        className={cn(isBook && "transition-opacity hover:opacity-85")}
      />
      {showAnyText && (
        <>
          <path
            d={`M${connectorStartX},${midY} L${connectorEndX},${midY}`}
            stroke={accent}
            strokeOpacity={0.28}
            strokeWidth={1}
            strokeLinecap="butt"
          />
          <circle
            cx={connectorEndX}
            cy={midY}
            r={2}
            fill={accent}
            fillOpacity={0.75}
          />
          {showBookHint && (
            <text
              x={textX}
              y={eyebrowY}
              textAnchor={textAnchor}
              dominantBaseline="middle"
              fontSize={8}
              fontWeight={700}
              letterSpacing={0.8}
              fill={SANKEY_LABEL_FILL}
            >
              PORTFOLIO
            </text>
          )}
          <text
            x={textX}
            y={labelY}
            textAnchor={textAnchor}
            dominantBaseline="middle"
            fill={SANKEY_LABEL_FILL}
            fontSize={11}
            fontWeight={600}
          >
            {displayLabel}
          </text>
          <text
            x={textX}
            y={amountY}
            textAnchor={textAnchor}
            dominantBaseline="middle"
            fill={SANKEY_AMOUNT_FILL}
            fontSize={10}
            fontWeight={500}
          >
            {amountText}
          </text>
        </>
      )}
    </g>
  );
}

export interface BudgetSankeyChartProps {
  data: { nodes: BudgetPlanNode[]; links: { source: number; target: number; value: number }[] };
  currencyCode: string;
  onBookNodeClick: (payload: { id?: string }) => void;
}

export function BudgetSankeyChart({
  data,
  currencyCode,
  onBookNodeClick,
}: BudgetSankeyChartProps) {
  return (
    <div className="h-[600px] w-full min-h-[600px]">
      <ResponsiveContainer width="100%" height="100%">
        <Sankey
          data={data as any}
          nodeWidth={14}
          nodePadding={56}
          margin={{ top: 20, right: 116, bottom: 20, left: 116 }}
          linkCurvature={0.4}
          sort={false}
          link={(linkProps: any) => <BudgetSankeyLink {...linkProps} />}
          node={(nodeProps: any) => (
            <BudgetSankeyNode
              {...nodeProps}
              currencyCode={currencyCode}
              onNodeClick={onBookNodeClick}
            />
          )}
        >
          <Tooltip content={<BudgetSankeyTooltip currencyCode={currencyCode} />} />
        </Sankey>
      </ResponsiveContainer>
    </div>
  );
}
